import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type IdeaModel from 'scaleflow/models/idea';
import IdeaValidations from 'scaleflow/validators/idea';
import ImageUploader from 'quill2-image-uploader';
import ImageFormat from 'scaleflow/utils/quill-extenstions/image-format';
import MarkdownFormat from 'scaleflow/utils/quill-extenstions/markdown-format';
import imageResizeOptions from 'scaleflow/utils/quill-extenstions/image-resize';
import Quill from 'quill';
import registerIfNotRegistered from 'scaleflow/utils/quill-extenstions/registerIfNotRegistered';
import type StoreService from 'scaleflow/services/store';
import type SessionService from 'scaleflow/services/session';
import type CommentModel from 'scaleflow/models/comment';
import type Delta from 'quill-delta';
import { assert } from '@ember/debug';
import { hideAll } from 'tippy.js';
import type FeatureAccessService from 'scaleflow/services/feature-access';
import type OrganisationService from 'scaleflow/services/organisation';
import type Owner from '@ember/owner';
import capitalize from 'scaleflow/utils/capitalize';
import imageUploadHandler from 'scaleflow/utils/quill-extenstions/image-uploader';

Quill.register('formats/image', ImageFormat, true);
registerIfNotRegistered(Quill, 'modules/imageUploader', ImageUploader);
registerIfNotRegistered(Quill, 'modules/markdown', MarkdownFormat);
interface Tab {
  label: string;
  key: string;
}

export interface IdeasIdeaSummaryPanelSignature {
  Args: {
    idea: IdeaModel;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class IdeasIdeaSummaryPanelComponent extends Component<IdeasIdeaSummaryPanelSignature> {
  ideaValidations = IdeaValidations;

  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare currentUser: any;
  @service declare featureAccess: FeatureAccessService;
  @service declare session: SessionService;
  @service declare organisation: OrganisationService;

  @tracked tab = 'comments';
  @tracked comment: null | CommentModel = null;
  @tracked deleteConfirmationModalIsVisible = false;

  constructor(owner: Owner, args: IdeasIdeaSummaryPanelSignature['Args']) {
    super(owner, args);

    this.comment = this.initializeNewComment();
  }

  get tabs(): Tab[] {
    return [
      {
        label: capitalize(this.intl.t('dictionary.comments', { num: 2 })),
        key: 'comments',
      },
      {
        label: this.intl.t('dictionary.details'),
        key: 'details',
      },
    ];
  }

  initializeNewComment(): CommentModel {
    const comment = this.store.createRecord<CommentModel>('comment', {
      author: this.currentUser._currentUser,
      created_at: new Date().toISOString(),
    });
    return comment;
  }

  @action
  setTab(tab: string): void {
    this.tab = tab;
  }

  @action
  deleteIdea(): void {
    try {
      this.router.replaceWith('backlog.ideas');
      void this.args.idea.destroyRecord();
    } catch (err) {
      console.log(err);
    }
  }

  get modules() {
    const modules: any = {};
    modules.imageUploader = {
      upload: (file: File) => {
        return imageUploadHandler(
          this.session.idToken as string,
          this.organisation.id as string,
          file,
        ).then((s3Link: string) => {
          return s3Link;
        });
      },
    };

    modules.resize = imageResizeOptions(this.intl);
    modules.markdown = true;

    const toolbarOptions = [
      ['bold', 'italic', 'strike'], // toggled buttons
      ['blockquote', 'code-block'],

      [{ header: 1 }, { header: 2 }], // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      ['link', 'image', 'clean'], // remove formatting button
    ];
    modules.toolbar = toolbarOptions;
    return modules;
  }

  @action
  setComment(e: Delta) {
    if (!this.comment) assert('New comment object was not initialized');
    this.comment.text_delta = e;
  }

  @action
  saveComment() {
    if (
      this.comment?.text_delta !== undefined &&
      this.comment.author !== undefined
    ) {
      this.comment.idea = this.args.idea;
      this.comment
        .save()
        .then(() => {
          this.comment = this.initializeNewComment();
        })
        .catch((err) => console.debug(err));
    }
  }

  @action
  clearDeadline(idea: IdeaModel) {
    idea.deadline = undefined;
    void idea.save();
  }

  @action
  showDeleteConfirmationModal() {
    hideAll();
    this.deleteConfirmationModalIsVisible = true;
  }

  @action
  handleDeleteConfirmation() {
    this.deleteConfirmationModalIsVisible = false;
    this.deleteIdea();
  }

  @action
  closeDeleConfirmationModal() {
    this.deleteConfirmationModalIsVisible = false;
  }

  willDestroy(): void {
    super.willDestroy();
    this.comment = null;
  }
}
